import type { CSSProperties } from 'react';
import { initialsOf, t, type BannerAvatar, type IconMeaning } from '@bms2/shared';
import { Icon } from '../../components/Icon';
import { IconButton } from '../../components/controls';
import styles from './RecordBannerHead.module.css';

/**
 * The picture slot at the left of every record banner (piece 13, decision 0616;
 * T170). 🔒 **One rule for every record type, and it is here.** A picture where
 * the record has one; a Person's initials on their `avatarColor` where they have
 * none; and for everything else the record type's own glyph inside the same round
 * slot — the default avatar. The camera badge is drawn only where this reader may
 * change the picture, which is a Person and a Company and nothing else.
 *
 * 🔨 The default avatar is not an empty slot and not the bare glyph beside the
 * name. Every record banner draws the same circle in the same place, so a
 * document's title bar and a Person's read as one standard (product owner,
 * 2026-09-22: *"Make all title bars follow the feel of the changes for person and
 * company"*).
 */
export interface BannerAvatarSlotProps {
  /** What the record's own answer says to draw; absent where the record type carries no picture at all. */
  avatar?: BannerAvatar | null;
  /** The record type's glyph, which is what the slot draws where there is no picture and no initials. */
  glyph: IconMeaning;
  /** The name the initials come from and the picture is labelled by. */
  title: string;
  apiBaseUrl?: string;
  /** Opens the picture upload; absent where the record's picture is not set here. */
  onSetPhoto?: (() => void) | null;
}

export function BannerAvatarSlot({ avatar = null, glyph, title, apiBaseUrl = '', onSetPhoto = null }: BannerAvatarSlotProps) {
  // The colour is a palette token name, drawn as given and never derived here, the way the Attendee Avatar draws it (decision 0055).
  const style = avatar?.avatarColor ? ({ '--avatar-fill': `var(--${avatar.avatarColor})` } as CSSProperties) : undefined;
  const badge = avatar?.editable && onSetPhoto ? onSetPhoto : null;
  const imageFileId = avatar?.imageFileId ?? null;
  // 🔨 Initials are a Person's answer and nobody else's: they come with avatarColor, which only a Person carries.
  const initials = imageFileId === null && avatar?.avatarColor ? initialsOf(title) : null;
  return (
    <span className={styles.wrap} data-banner-avatar={imageFileId ?? (initials === null ? 'default' : 'initials')}>
      <span className={styles.circle} style={style} role="img" aria-label={title} data-avatar-fill={avatar?.avatarColor ?? 'none'}>
        {imageFileId ? (
          // 🔨 A picture that will not load hides itself rather than drawing a broken-image glyph over the banner's gradient.
          <img className={styles.photo} src={`${apiBaseUrl}/files/${imageFileId}/stream`} alt={t('record.picture.alt')} onError={(event) => { event.currentTarget.hidden = true; }} />
        ) : initials !== null ? (
          <span className={styles.initials} aria-hidden="true">{initials}</span>
        ) : (
          /* The default avatar: the record type's glyph in the same circle, no badge. */
          <span className={styles.glyph} aria-hidden="true" data-banner-avatar-glyph={glyph}>
            <Icon meaning={glyph} size="banner" />
          </span>
        )}
      </span>
      {/* 🔒 The camera badge is the one icon button control (T178); the slot around it is what puts it on the circle's lower-right diagonal. */}
      {badge ? (
        <span className={styles.badgeSlot}>
          <IconButton meaning="image" label={t('record.picture.open')} size="fit" tone="badge" name="banner-avatar-badge" onClick={badge} />
        </span>
      ) : null}
    </span>
  );
}
